import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { CheckCircle, Spinner, Tooltip, XCircle } from "@opendate-app/setlist";

const errorsContainerStyle = {
  alignItems: "center",
  color: "var(--sl-text-secondary)",
  display: "flex",
  flexDirection: "row",
  fontSize: "14px",
  gap: "8px",
};

export const parseLiquid = async (template, sampleData, teamId, id, csrfToken) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
  const response = await axios.post(`/teams/${teamId}/beefree_templates/${id}/parse_liquid`, {
    beefree_template: {
      template: template,
      sample_data: sampleData,
    }
  });
  return response.data;
};

export const parseLiquidAdminTemplate = async (template, sampleData, id, csrfToken) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
  const response = await axios.post(`/admin/beefree_templates/${id}/parse_liquid_template`, {
    beefree_template: {
      template: template,
      sample_data: sampleData,
    }
  });
  return response.data;
};

export const parseLiquidTemplate = async (template, sampleData, teamId, id, csrfToken, venueOwnershipId = null) => {
  axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
  const response = await axios.post(`/teams/${teamId}/beefree_templates/${id}/parse_liquid_template`, {
    beefree_template: {
      template: template,
      sample_data: sampleData,
      venue_ownership_id: venueOwnershipId,
    }
  });
  return response.data;
};

const compactStyle = {
  ...errorsContainerStyle,
  color: "inherit",
  fontSize: "inherit",
  gap: "4px",
};

const ErrorsContainer = ({
  templatableContext,
  compact = false,
}) => {
  const {
    csrfToken,
    team,
    template,
    body,
    sampleData,
    isEmailTemplate,
    errors,
    setErrors,
    beefreeTemplate,
    setProcessedTemplate,
    venueOwnership,
    isAdminBlueprint,
  } = templatableContext();

  const [isParsing, setIsParsing] = useState(false);
  const templateVersion = useRef(0);

  useEffect(() => {
    setIsParsing(true);
    templateVersion.current += 1;
    const currentVersion = templateVersion.current;

    const delayDebounce = setTimeout(() => {
      if (isAdminBlueprint) {
        parseLiquidAdminTemplate(template, sampleData, beefreeTemplate.id, csrfToken).then(({ liquidErrors, processedTemplate }) => {
          if (currentVersion === templateVersion.current) {
            setProcessedTemplate(processedTemplate);
            setErrors(liquidErrors);
            setIsParsing(false);
          }
        });
      } else if (isEmailTemplate) {
        parseLiquidTemplate(template, sampleData, team.id, beefreeTemplate.id, csrfToken, venueOwnership?.id).then(({ liquidErrors, processedTemplate }) => {
          if (currentVersion === templateVersion.current) {
            setProcessedTemplate(processedTemplate);
            setErrors(liquidErrors);
            setIsParsing(false);
          }
        });
      } else {
        parseLiquid(body, sampleData, team.id, beefreeTemplate.id, csrfToken).then(({ parsedText, liquidErrors }) => {
          if (currentVersion === templateVersion.current) {
            setParsedBody(parsedText);
            setErrors(liquidErrors);
            setIsParsing(false);
          }
        });
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [template, body, sampleData]);

  return (
    isParsing ? (
      <Spinner size="sm" aria-label="Checking for template errors" />
    ) : (
      <div style={compact ? compactStyle : errorsContainerStyle}>
          {errors?.length > 0 ? (
            <Tooltip title="Liquid errors" supportingText={errors.join(" · ")}>
              <span style={{ display: "inline-flex" }} tabIndex={0}>
                <XCircle width={compact ? 12 : 18} height={compact ? 12 : 18} color="var(--sl-fg-error)" aria-label="Template has errors" />
              </span>
            </Tooltip>
          ) : (
            <CheckCircle
              width={compact ? 12 : 18}
              height={compact ? 12 : 18}
              color={compact ? "currentColor" : "var(--sl-fg-success)"}
              aria-hidden="true"
            />
          )}
          <div>
            {errors?.length || "No"} {compact ? "errors" : "Errors"}
          </div>
      </div>
    )
  );
};

export default ErrorsContainer;
